import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const SECTIONS = [
  ["Client / dates", [
    ["client_first_name", "Client first name"], ["client_last_name", "Client last name"],
    ["effective_date", "Questionnaire effective date", "date"], ["proposed_start_date", "Proposed start date", "date"],
    ["dob", "Date of birth", "date"], ["phone", "Client phone"], ["address", "Street address"],
    ["city", "City"], ["state", "State"], ["zip", "ZIP"], ["proposed_schedule", "Proposed schedule"],
  ]],
  ["Home / safety", [
    ["lives_alone", "Lives alone"], ["household_members", "Who lives in the home"],
    ["oxygen", "Oxygen therapy"], ["contact_precautions", "Contact precautions"], ["covid", "COVID symptoms"],
    ["weapons", "Unsecured weapons/firearms"], ["smoking", "Smoking inside home"], ["pets", "Pets on premises"],
    ["minors", "Anyone under 18"], ["hazards", "Home hazards"], ["hazard_notes", "Yes-answer details"],
  ]],
  ["Caller / representative", [
    ["caller_name", "Caller name"], ["caller_phone", "Caller phone"], ["referral_relationship", "Referral relationship"],
    ["caregiver_request", "Caregiver to train/hire"], ["has_authorized_rep", "Authorized representative?"],
    ["rep_name", "Representative name"], ["rep_phone", "Representative phone"], ["rep_email", "Representative email"],
    ["rep_note", "Representative note"],
  ]],
  ["Payer / service coordinator", [
    ["payer_raw", "Payment source"], ["medicaid_id", "Medicaid ID"], ["sc_entity", "SC entity"],
    ["sc_name", "Service coordinator"], ["sc_phone", "SC phone"], ["sc_email", "SC email"], ["submission_id", "Submission ID"],
  ]],
];

export function IntakeQuestionnaireCard({ clientId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/clients/${clientId}/intake-questionnaire`).then((r) => setData(r.data.questionnaire || {})).catch((e) => toast.error(formatApiError(e)));
  }, [clientId]);

  if (!data) return null;
  const set = (k) => (e) => setData({ ...data, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      const res = await api.put(`/clients/${clientId}/intake-questionnaire`, data);
      setData(res.data.questionnaire || {});
      toast.success("Intake questionnaire saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="intake-questionnaire-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-heading text-sm font-semibold text-stone-900">Intake Questionnaire</p>
          <p className="mt-0.5 text-xs text-stone-500">Imported from the completed Initial Contact questionnaire when a matching row exists. Existing intake fields were not overwritten.</p>
        </div>
        {data.source_file && <span className="rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-[10px] font-semibold text-stone-500" data-testid="intake-questionnaire-source">Imported · {data.submission_id || "source on file"}</span>}
      </div>
      {!data.submission_id && <p className="mt-4 rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/10 p-3 text-xs text-[#8a5d14]" data-testid="intake-questionnaire-empty">No completed questionnaire row matched this client. You may enter one manually.</p>}
      {SECTIONS.map(([title, fields]) => (
        <div key={title} className="mt-5">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-400">{title}</p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {fields.map(([key, label, type]) => (
              <div key={key} className={key === "hazard_notes" || key === "caregiver_request" || key === "rep_note" ? "md:col-span-2" : ""}>
                <Label className="text-xs">{label}</Label>
                <Input type={type || "text"} value={data[key] || ""} onChange={set(key)} className="mt-1 border-stone-300 bg-white" data-testid={`intake-questionnaire-${key}`} />
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="mt-4 space-y-1.5">
        <Label className="text-xs">Questionnaire notes</Label>
        <textarea value={data.notes || ""} onChange={set("notes")} rows={3} className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm" data-testid="intake-questionnaire-notes" />
      </div>
      <div className="mt-4 flex justify-end">
        <Button onClick={save} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="intake-questionnaire-save">{busy ? "Saving…" : "Save questionnaire"}</Button>
      </div>
    </div>
  );
}
